import { useSignal, type Signal, signal, useComputed } from "@preact/signals";
import { html } from "htm/preact";
import { createContext } from "preact";
import { type FunctionComponent } from "preact";
import { useContext, useEffect, useMemo } from "preact/hooks";

const changed = signal(Date.now());

export type ThemeContextValue = {
  prefersDarkColorScheme: Signal<boolean>;
};

export const ThemeContext = createContext<ThemeContextValue>({
  prefersDarkColorScheme: signal(false),
}); /* c8 ignore next */

export const ThemeProvider: FunctionComponent = ({ children }) => {
  const prefersDarkColorScheme = usePrefersDarkColorScheme();
  const value = useSignal<ThemeContextValue>({
    prefersDarkColorScheme,
  });
  // NOTE: Preact optimizes signals passed directly to attributes (scheme vs scheme.value)
  // to prevent re-renders
  const scheme = useComputed(() =>
    prefersDarkColorScheme.value ? "dark" : "auto",
  );
  return html`
    <div class="Theme" data-scheme=${scheme}>
      <${ThemeContext.Provider} value=${value.value}>${children}<//>
    </div>
  `;
};

export function themeChanged() {
  changed.value = Date.now();
}

export const usePrefersDarkColorSchemeMedia = "(prefers-color-scheme: dark)";

export function usePrefersDarkColorScheme() {
  /* c8 ignore next */
  if (typeof matchMedia === "undefined") return useSignal(false);
  const media = useMemo(() => matchMedia(usePrefersDarkColorSchemeMedia), []);
  const prefersDark = useSignal(media.matches);
  useEffect(() => {
    const handler = () => {
      prefersDark.value = media.matches;
    };
    media.addEventListener("change", handler);
    return () => {
      media.removeEventListener("change", handler);
    };
  }, []);
  return prefersDark;
}

export function useTheme() {
  return useContext(ThemeContext);
}

export function useThemeEffect(effect: () => void) {
  const theme = useTheme();
  useEffect(effect, [changed.value, theme.prefersDarkColorScheme.value]);
}
